import { z } from 'zod';
import { ControllerRenderProps } from 'react-hook-form';
import { optionalUsPhoneField } from '@/utils/phoneForm';

export const providerFacilitySchema = z
  .object({
    id: z.number().optional(),
    providerName: z.string().optional(),
    facilityName: z.string().optional(),
    providerNpi: z.string().optional(),
    facilityNpi: z.string().optional(),
    specialty: z
      .string()
      .max(255, 'Specialty must be 255 characters or less')
      .optional(),
    address1: z.string().optional(),
    address2: z.string().optional(),
    city: z.string().optional(),
    state: z
      .string()
      .transform((v) => (v?.trim() === '' ? undefined : v))
      .refine((v) => !v || v.length === 2, {
        message: 'State name must be exactly 2 characters',
      })
      .optional(),
    postalCode: z
      .string()
      .optional()
      .refine((val) => !val || /^[0-9]{5}$/.test(val), {
        message: 'Enter a valid 5-digit zip code',
      }),
    phoneNumber: optionalUsPhoneField('Phone number'),
    faxNumber: optionalUsPhoneField('Fax number'),
    recordsRequested: z.enum(['complete', 'specific']),
    serviceDateRange: z.enum(['6months', '1year', 'custom']),
    serviceStartDate: z.string().optional(),
    serviceEndDate: z.string().optional(),
    medicalRecordDetails: z.string().nullish(),
  })
  .superRefine((data, ctx) => {
    const providerName = (data.providerName || '').trim();
    const facilityName = (data.facilityName || '').trim();
    if (!providerName && !facilityName) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Add a provider or facility.',
        path: ['providerName'],
      });
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Select a provider or facility.',
        path: ['facilityName'],
      });
    }
  })
  .refine(
    (data) =>
      data.recordsRequested !== 'specific' || !!data.medicalRecordDetails,
    {
      message: 'Enter specific medical record details.',
      path: ['medicalRecordDetails'],
    },
  )
  .refine(
    (data) => data.serviceDateRange !== 'custom' || !!data.serviceStartDate,
    {
      message: 'Select a date.',
      path: ['serviceStartDate'],
    },
  )
  .refine(
    (data) => data.serviceDateRange !== 'custom' || !!data.serviceEndDate,
    {
      message: 'Select a date.',
      path: ['serviceEndDate'],
    },
  )
  .refine(
    (data) => {
      if (
        data.serviceDateRange !== 'custom' ||
        !data.serviceStartDate ||
        !data.serviceEndDate
      ) {
        return true;
      }
      const startDate = new Date(data.serviceStartDate);
      const endDate = new Date(data.serviceEndDate);
      return startDate <= endDate;
    },
    {
      message: 'Service end date must be after or equal to start date.',
      path: ['serviceEndDate'],
    },
  );

export type ProviderFacilityFormData = z.infer<typeof providerFacilitySchema>;

export type ProviderFacilityFormFieldProps<
  T extends keyof ProviderFacilityFormData,
> = {
  field: ControllerRenderProps<ProviderFacilityFormData, T>;
};
